import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TTableColumn, DateTools } from '@treezor/design-system';

import { IOnboardingUser, TOnboardingUserType } from '@/store/models/onboarding-user.model';
import { DashboardHelpers } from '@/utils/DashboardHelpers';

const completedOnboardingUserColumn = ['email', 'created', 'profile'] as const;

const onboardingUserColumn = ['email', 'title', 'firstname', 'lastname'] as const;

type TRow =
  | Record<(typeof completedOnboardingUserColumn)[number], ReactNode>
  | Record<(typeof onboardingUserColumn)[number], ReactNode>;

interface IUseOnboardingUserTable {
  type: TOnboardingUserType;
  onboardingUsers: IOnboardingUser[];
}

export function useOnboardingUserTable({ type, onboardingUsers }: IUseOnboardingUserTable) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const headers: TTableColumn<TRow>[] = (
    type === 'completed' ? completedOnboardingUserColumn : onboardingUserColumn
  ).map((field) => ({
    name: field,
    field,
    label: t(`pages.administration.onBoarding.table.${field}`),
  }));

  const rows = onboardingUsers?.map((onboardingUser) => {
    const email = onboardingUser?.trzConnectUserOnboarding?.email || onboardingUser.username;
    const onClick = onboardingUser?.userId
      ? () => navigate(DashboardHelpers.generateProfilePath(onboardingUser.userId))
      : null;

    return type === 'completed'
      ? {
          email,
          created: DateTools.format(onboardingUser?.clients?.[0]?.acceptedAt, 'date-time') || '-',
          profile: onboardingUser?.userId || '-',
          onClick,
        }
      : {
          email,
          title: onboardingUser?.trzConnectUserOnboarding?.title,
          firstname: onboardingUser?.trzConnectUserOnboarding?.firstname,
          lastname: onboardingUser?.trzConnectUserOnboarding?.lastname,
          onClick,
        };
  });

  return { headers, rows };
}
